import React, { useEffect, useRef, useState } from "react";
import {
  Box,
  Table,
  TableHead,
  TableBody,
  TableCell,
  TableRow,
  Typography,
  Paper,
  Modal,
  TableContainer,
  Grid,
  IconButton,
  Divider,
  Button,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField
} from "@mui/material";
// import CloseIcon from '@mui/icons-material/Close'; // Import a proper close icon
// import LocalPrintshopIcon from '@mui/icons-material/LocalPrintshop'; // Icon for print button
import { MdPrint, MdClose, MdDownload, MdMail, MdSend, MdSendToMobile, MdSendAndArchive, MdViewCompact } from 'react-icons/md';
import labones from '../../assets/images/labones.png';
import rosevine from '../../assets/images/rosevine.png';
import validmed from '../../assets/images/validmed.png';
import primedews from '../../assets/images/primedews.png';
import lifecare from '../../assets/images/lifecare.png';
import sterling from '../../assets/images/sterling.png';
import { styled } from '@mui/material/styles'; // Import styled for custom components
import { get, post } from "../../utility/fetch";
import { calculateAge } from "../../utility/general";

// Import jsPDF and html2canvas
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import { FaMailchimp } from "react-icons/fa";
import { HiMail } from "react-icons/hi";
import { LabReportPage } from "../pages/LabReportPage";
import LabReportDesigner from "../LabReportDesigner";



// Styled components for visual consistency (from previous beautiful version)
const StyledModalBox = styled(Box)(({ theme }) => ({
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: '90%',
  maxWidth: 1000,
  backgroundColor: '#f8fbf9', // Overall very light green background
  boxShadow: theme.shadows[24], // Stronger shadow for the modal
  padding: theme.spacing(4),
  borderRadius: theme.shape.borderRadius * 2, // More rounded corners
  fontFamily: "Arial, sans-serif", // Ensure font consistency
  maxHeight: '95vh', // Limit modal height
  overflowY: 'auto', // Enable scrolling within modal if content overflows
}));

const ReportContentBox = styled(Box)(({ theme }) => ({
  padding: theme.spacing(3), // Padding for the printable content
  backgroundColor: '#ffffff', // White background for the report content itself
  borderRadius: theme.shape.borderRadius,
  border: '1px solid #e0e0e0', // Subtle border
}));

const ReportHeaderTypography = styled(Typography)(({ theme }) => ({
  color: "#2e7d32", // Darker green for main heading
  fontWeight: 'bold',
  fontFamily: "'Roboto Condensed', sans-serif",
  textAlign: 'center',
  marginBottom: theme.spacing(2),
}));

const SubHeaderTypography = styled(Typography)(({ theme }) => ({
  color: "#2e7d32",
  fontWeight: "bold",
  fontFamily: "'Roboto Condensed', sans-serif",
  fontSize: '1.25rem', // Slightly larger for sub-headers
}));

const SectionTitleTypography = styled(Typography)(({ theme }) => ({
  color: '#2e7d32',
  marginBottom: theme.spacing(1),
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1),
}));

const InfoTextTypography = styled(Typography)(({ theme }) => ({
  color: '#555',
  '& strong': {
    color: '#2e7d32',
  },
}));

const VitalSignsPaper = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(3),
  marginBottom: theme.spacing(3),
  backgroundColor: '#e8f5e9', // Lighter green for vital signs background
  borderRadius: theme.shape.borderRadius * 2,
  boxShadow: theme.shadows[2], // Subtle shadow for paper
}));

const VitalItemBox = styled(Box)(({ theme }) => ({
  backgroundColor: '#f1f8e9',
  padding: theme.spacing(1.5),
  borderRadius: theme.shape.borderRadius,
  height: '100%',
}));

const TableHeaderCell = styled(TableCell)(({ theme }) => ({
  backgroundColor: "#2e7d32", // Dark green for table headers
  color: 'white',
  fontWeight: 'bold',
  fontSize: '0.95rem',
}));

const StyledTableRow = styled(TableRow)(({ theme }) => ({
  '&:nth-of-type(odd)': {
    backgroundColor: '#f8fbf9', // Very light green for odd rows
  },
  '&:hover': {
    backgroundColor: '#e8f5e9', // Slightly darker green on hover
  },
  '&:last-child td': {
    borderBottom: 0,
  },
}));

const ResultTableCell = styled(TableCell)(({ theme }) => ({
  fontWeight: 'bold',
  color: '#2e7d32',
}));

const FlagTableCell = styled(TableCell)(({ theme, isflagged }) => ({
  fontWeight: 'bold',
  color: isflagged ? theme.palette.error.main : '#2e7d32', // Red for flagged, dark green for normal
}));


// Helper function for reference range content
const getRefRangeContent = (details) => {
  const {
    normalRefRange,
    refRange_Male,
    refRange_FeMale,
    refRange_Elderly,
    refRange_Adult,
    refRange_Children,
  } = details;

  const hasGenderRanges =
    refRange_Male &&
    refRange_FeMale &&
    refRange_Elderly &&
    (refRange_Male !== normalRefRange || refRange_FeMale !== normalRefRange || refRange_Elderly !== normalRefRange);
  const hasAgeRanges =
    refRange_Adult &&
    refRange_Children &&
    (refRange_Adult !== normalRefRange || refRange_Children !== normalRefRange);


  if (hasGenderRanges) {
    return (
      <Table size="small" sx={{ mb: 1, minWidth: '250px' }}>
        <TableHead>
          <TableRow sx={{ backgroundColor: '#3C7E2D' }}>
            <TableCell align="center" sx={{ color: 'white', fontWeight: 'bold', py: 1 }}>MALE</TableCell>
            <TableCell align="center" sx={{ color: 'white', fontWeight: 'bold', py: 1 }}>FEMALE</TableCell>
            <TableCell align="center" sx={{ color: 'white', fontWeight: 'bold', py: 1 }}>ELDERLY</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          <TableRow>
            <TableCell align="center" sx={{ py: 1 }}>{refRange_Male || '-'}</TableCell>
            <TableCell align="center" sx={{ py: 1 }}>{refRange_FeMale || '-'}</TableCell>
            <TableCell align="center" sx={{ py: 1 }}>{refRange_Elderly || '-'}</TableCell>
          </TableRow>
        </TableBody>
      </Table>
    );
  }

  if (hasAgeRanges) {
    return (
      <Table size="small" sx={{ mb: 1, minWidth: '200px' }}>
        <TableHead>
          <TableRow sx={{ backgroundColor: '#3C7E2D' }}>
            <TableCell align="center" sx={{ color: 'white', fontWeight: 'bold', py: 1 }}>ADULT</TableCell>
            <TableCell align="center" sx={{ color: 'white', fontWeight: 'bold', py: 1 }}>CHILDREN</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          <TableRow>
            <TableCell align="center" sx={{ py: 1 }}>{refRange_Adult || '-'}</TableCell>
            <TableCell align="center" sx={{ py: 1 }}>{refRange_Children || '-'}</TableCell>
          </TableRow>
        </TableBody>
      </Table>
    );
  }

  return (
    <Typography variant="body2" sx={{ color: '#555' }}>
      {normalRefRange || "-"}
    </Typography>
  );
};

// Robust flag checking logic (from previous version)
const checkRangeAndGetFlag = (resultStr, testDetails, selectedRangeType) => {
  const result = parseFloat(resultStr);
  if (isNaN(result)) {
    return '';
  }

  let refRangeStr = testDetails[selectedRangeType];
  if (!refRangeStr || refRangeStr.trim() === '') {
    refRangeStr = testDetails.normalRefRange;
  }

  if (!refRangeStr || refRangeStr.trim() === '' || refRangeStr === '-') {
    return '';
  }

  const cleanedRange = refRangeStr.toLowerCase().replace(/mm\/hr|%|x ?10\^3\/ul|g\/dl|fl|pg|mg\/dl/g, '').trim();

  let lowerBound = -Infinity;
  let upperBound = Infinity;

  if (cleanedRange.includes('-')) {
    const parts = cleanedRange.split('-').map(s => parseFloat(s.trim()));
    lowerBound = isNaN(parts[0]) ? -Infinity : parts[0];
    upperBound = isNaN(parts[1]) ? Infinity : parts[1];
  } else if (cleanedRange.startsWith('<=')) {
    upperBound = parseFloat(cleanedRange.substring(2).trim());
  } else if (cleanedRange.startsWith('<')) {
    upperBound = parseFloat(cleanedRange.substring(1).trim()) - Number.EPSILON;
  } else if (cleanedRange.startsWith('>=')) {
    lowerBound = parseFloat(cleanedRange.substring(2).trim());
  } else if (cleanedRange.startsWith('>')) {
    lowerBound = parseFloat(cleanedRange.substring(1).trim()) + Number.EPSILON;
  } else {
    const singleValue = parseFloat(cleanedRange);
    if (!isNaN(singleValue)) {
      lowerBound = singleValue;
      upperBound = singleValue;
    } else {
      return '';
    }
  }

  if (result < lowerBound) {
    return 'LOW';
  } else if (result > upperBound) {
    return 'HIGH';
  } else {
    return 'NORMAL';
  }
};


const LabReportTable = ({ patientInfo, review, template, open, walkin, onClose, vitalInfo, color, currentTemplate }) => {
  const [patientTestReports, setPatientTestReports] = useState([]);
  const [loading, setLoading] = useState(false);
  const [sendEmailOpen, setSendEmailOpen] = useState(false);
  const [emailForm, setEmailForm] = useState({
    name: '',
    email: ''
  });
  const [sending, setSending] = useState(false);
  const [emailError, setEmailError] = useState('');
  const [age, setAge] = useState(null);

  // compact view state - local to this modal instance
  const [compact, setCompact] = useState(false);

  // Ref for the content you want to print (only the report content, not modal chrome)
  const reportContentRef = useRef();

  // provider info from sessionStorage (logo/address/name)
  const providerLogo = sessionStorage.getItem("profilePic") || null;
  const providerName = sessionStorage.getItem("healthCareProviderName") || "Healthcare Provider";
  const providerAddress =
    sessionStorage.getItem("healthCareProviderAddress") ||
    "123 Medical Avenue, Suite 4 • CityName • Country • +1 (555) 123-4567";

  // helper: pick provider logo (supports imported images, data-urls and raw base64 stored in sessionStorage)
  // const getProviderLogo = () => {
  //   const name = (sessionStorage.getItem('healthCareProviderName') || '').toString();
  //   if (name.includes('LAB ONE')) return labones;
  //   if (name.includes('Life Care Turkish Hospital')) return lifecare;
  //   if (name.includes('Rosevine')) return rosevine;
  //   if (name.includes('Validmed')) return validmed;
  //   if (name.includes('PrimeDews')) return primedews;
  //   if (name.includes('Sterling')) return sterling;

  //   const pic = sessionStorage.getItem('profilePic');
  //   if (!pic) return '/default-logo.png';

  //   // already a data URL?
  //   if (pic.startsWith('data:')) return pic;

  //   // raw base64 string -> convert to data URL
  //   if (/^[A-Za-z0-9+/=\s]+$/.test(pic) && pic.length > 100) {
  //     // assume PNG if no mime provided
  //     return `data:image/png;base64,${pic.replace(/\s+/g, '')}`;
  //   }

  //   // otherwise treat as a normal URL (may be CORS-limited)
  //   return pic;
  // };

  // new helper: get letterhead data url from sessionStorage (checks several possible keys)
  const getLetterheadDataUrl = () => {
    const keysToCheck = ['letterhead', 'letterHead', 'providerLetterhead', 'letterHeadBase64', 'logoBase64'];
    let pic = null;
    for (const k of keysToCheck) {
      const v = sessionStorage.getItem(k);
      if (v) { pic = v; break; }
    }
    if (!pic) return null;

    if (pic.startsWith('data:')) return pic;

    if (/^[A-Za-z0-9+/=\s]+$/.test(pic) && pic.length > 100) {
      return `data:image/png;base64,${pic.replace(/\s+/g, '')}`;
    }

    return pic; // fallback URL
  };

  const handleDownloadPdf = async () => {
    const input = reportContentRef.current;
    if (!input) return console.error("Report content ref is not available.");

    try {
      // clone and prepare content (same as before)
      const cloned = input.cloneNode(true);
      const letterheadData = getLetterheadDataUrl();
      if (letterheadData) {
        const existing = cloned.querySelector('img[data-letterhead="true"]') || cloned.querySelector('.letterhead-image');
        if (!existing) {
          const img = document.createElement('img');
          img.setAttribute('data-letterhead', 'true');
          img.src = letterheadData;
          img.style.width = '100%';
          img.style.objectFit = 'cover';
          img.style.display = 'block';
          img.style.marginBottom = '12px';
          cloned.insertBefore(img, cloned.firstChild);
        } else {
          existing.src = letterheadData;
        }
      }

      cloned.style.width = 'auto';
      cloned.style.maxWidth = 'none';
      cloned.style.boxSizing = "border-box";
      cloned.style.position = "absolute";
      cloned.style.left = "-9999px";
      cloned.style.backgroundColor = window.getComputedStyle(input).backgroundColor || "#ffffff";

      const imgs = Array.from(cloned.getElementsByTagName('img'));
      imgs.forEach((img) => {
        try { img.crossOrigin = 'anonymous'; } catch (e) { /* ignore */ }
        if (img.getAttribute('data-letterhead') === 'true' && letterheadData) img.src = letterheadData;
      });

      document.body.appendChild(cloned);

      try { await document.fonts.ready; } catch (e) { /* ignore */ }
      await Promise.all(imgs.map(img => new Promise((resolve) => {
        if (!img.src) return resolve();
        if (img.complete) return resolve();
        img.onload = () => resolve();
        img.onerror = () => resolve();
      })));

      // capture full content to canvas
      const canvas = await html2canvas(cloned, {
        scale: Math.min(window.devicePixelRatio || 1.5, 2),
        useCORS: true,
        logging: false,
        backgroundColor: cloned.style.backgroundColor || "#ffffff",
        allowTaint: false,
        imageTimeout: 15000,
      });

      document.body.removeChild(cloned);

      // Paginate canvas into A4 pages in jsPDF
      const pdf = new jsPDF('p', 'pt', 'a4');
      const pdfWidth = pdf.internal.pageSize.getWidth();
      const pdfHeight = pdf.internal.pageSize.getHeight();

      const canvasWidth = canvas.width;
      const canvasHeight = canvas.height;

      // ratio canvas px to pdf pts
      const ratio = canvasWidth / pdfWidth;
      const pageHeightPx = Math.floor(pdfHeight * ratio);

      let remainingHeight = canvasHeight;
      let position = 0;
      let pageCount = 0;

      while (remainingHeight > 0) {
        const pageCanvas = document.createElement('canvas');
        pageCanvas.width = canvasWidth;
        const chunkHeight = Math.min(pageHeightPx, remainingHeight);
        pageCanvas.height = chunkHeight;

        const ctx = pageCanvas.getContext('2d');
        // draw the page chunk from source canvas
        ctx.drawImage(canvas, 0, position, canvasWidth, chunkHeight, 0, 0, canvasWidth, chunkHeight);

        const pageData = pageCanvas.toDataURL('image/png', 1.0);
        const pagePdfHeight = chunkHeight / ratio;

        if (pageCount > 0) pdf.addPage();
        pdf.addImage(pageData, 'PNG', 0, 0, pdfWidth, pagePdfHeight);

        remainingHeight -= chunkHeight;
        position += chunkHeight;
        pageCount++;
      }

      pdf.save(`Lab_Report_${new Date().toISOString().split('T')[0]}.pdf`);
    } catch (error) {
      console.error('PDF generation failed:', error);
      alert('Failed to generate PDF. Please try again.');
      const cloned = document.querySelector('[style*="-9999px"]');
      if (cloned) document.body.removeChild(cloned);
    }
  };

  const handleSendEmail = async () => {
    if (!emailForm.email || !emailForm.name) {
      setEmailError('Please fill in all fields');
      return;
    }

    if (!/\S+@\S+\.\S+/.test(emailForm.email)) {
      setEmailError('Please enter a valid email address');
      return;
    }

    const input = reportContentRef.current;
    if (!input) {
      setEmailError('Report content not available');
      return;
    }

    let cloned = null;
    try {
      setSending(true);
      setEmailError('');

      // Clone and prepare content
      cloned = input.cloneNode(true);
      const letterheadData = getLetterheadDataUrl();
      if (letterheadData) {
        const existing = cloned.querySelector('img[data-letterhead="true"]') || cloned.querySelector('.letterhead-image');
        if (!existing) {
          const img = document.createElement('img');
          img.setAttribute('data-letterhead', 'true');
          img.src = letterheadData;
          img.style.width = '100%';
          img.style.objectFit = 'cover';
          img.style.display = 'block';
          img.style.marginBottom = '12px';
          cloned.insertBefore(img, cloned.firstChild);
        } else {
          existing.src = letterheadData;
        }
      }

      // Make cloned node printable by canvas
      cloned.style.width = 'auto';
      cloned.style.maxWidth = 'none';
      cloned.style.boxSizing = 'border-box';
      cloned.style.position = 'absolute';
      cloned.style.left = '-9999px';
      cloned.style.backgroundColor = window.getComputedStyle(input).backgroundColor || '#ffffff';

      const imgs = Array.from(cloned.getElementsByTagName('img'));
      imgs.forEach((img) => {
        try { img.crossOrigin = 'anonymous'; } catch (e) { /* ignore */ }
        if (img.getAttribute('data-letterhead') === 'true' && letterheadData) img.src = letterheadData;
      });

      document.body.appendChild(cloned);

      // Wait for fonts and images to load
      try { await document.fonts.ready; } catch (e) { /* ignore */ }
      await Promise.all(imgs.map(img => new Promise((resolve) => {
        if (!img.src) return resolve();
        if (img.complete) return resolve();
        img.onload = () => resolve();
        img.onerror = () => resolve();
      })));

      // Create canvas from cloned content (fix: canvas must be created here)
      const canvas = await html2canvas(cloned, {
        scale: Math.min(window.devicePixelRatio || 1.5, 2),
        useCORS: true,
        logging: false,
        backgroundColor: cloned.style.backgroundColor || '#ffffff',
        allowTaint: false,
        imageTimeout: 15000,
      });

      // build PDF from canvas
      const imgData = canvas.toDataURL('image/png', 1.0);
      const pdf = new jsPDF('p', 'pt', [canvas.width, canvas.height]);
      pdf.addImage(imgData, 'PNG', 0, 0, canvas.width, canvas.height);

      // Get PDF as base64 (without data: prefix)
      const pdfBase64 = pdf.output('datauristring').split(',')[1];

      // Create URL-encoded payload (application/x-www-form-urlencoded)
      const urlEncodedData = new URLSearchParams();
      urlEncodedData.append('PatientName', emailForm.name);
      urlEncodedData.append('PatientEmail', emailForm.email);
      urlEncodedData.append('PdfReport', pdfBase64);

      // Send to endpoint with application/x-www-form-urlencoded
      const response = await fetch('https://api.greenzonetechnologies.com.ng/connectedhealthlabapi/api/patientlabreport/send-labreport', {
        method: 'POST',
        
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
          'Accept': 'application/json',
          'Authorization': `Bearer ${sessionStorage.getItem('token')}`,
        },
        body: urlEncodedData.toString()
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      // success
      setSendEmailOpen(false);
      setEmailForm({ name: '', email: '' });
      alert('Report sent successfully!');
    } catch (error) {
      console.error('Failed to send report:', error);
      setEmailError('Failed to send report. Please try again.');
    } finally {
      // cleanup cloned node and sending flag
      if (cloned && cloned.parentNode) document.body.removeChild(cloned);
      setSending(false);
    }
  };

  console.log(patientInfo);


  const getMyTemplate = async () => {
    let res = await get(`/labreporttemplate/lab-request/${sessionStorage.getItem("healthCareProviderId")}`);
    // console.log(res);
  }

  // provider logo from sessionStorage (supports imported images, data-urls and raw base64)
  // const getProviderLogo = () => {
  //   const name = (sessionStorage.getItem('healthCareProviderName') || '').toString();
  //   if (name.includes('LAB ONE')) return labones;
  //   if (name.includes('Life Care Turkish Hospital')) return lifecare;
  //   if (name.includes('Rosevine')) return rosevine;
  //   if (name.includes('Validmed')) return validmed;
  //   if (name.includes('PrimeDews')) return primedews;
  //   if (name.includes('Sterling')) return sterling;

  //   const pic = sessionStorage.getItem('profilePic');
  //   if (!pic) return '/default-logo.png';

  //   // already a data URL?
  //   if (pic.startsWith('data:')) return pic;

  //   // raw base64 string -> convert to data URL
  //   if (/^[A-Za-z0-9+/=\s]+$/.test(pic) && pic.length > 100) {
  //     // assume PNG if no mime provided
  //     return `data:image/png;base64,${pic.replace(/\s+/g, '')}`;
  //   }

  //   // otherwise treat as a normal URL (may be CORS-limited)
  //   return pic;
  // };


  // console.log(patientInfo)
  const getLabResults = async () => {
    try {
      setLoading(true);
      let res;
      if (walkin) {
        res = await get(`/walkinpatientlabreport/walk-in-lab-request/${patientInfo?.walkInLabRequestId}`);
      } else {
        res = await get(`/patientlabreport/internal-lab-report/lab-request/${patientInfo?.id}`);
      }

      console.log(res);
      setAge(res?.data?.age)
      setPatientTestReports(res?.data[0]?.patientTestReports || []);
    } catch (e) {
      setPatientTestReports([]);
    } finally {
      setLoading(false);
    }
  };

  const handlePrint = () => {
    window.print();
  };

  useEffect(() => {
    if (open) {
      getLabResults();
    }
    getMyTemplate();
  }, [open, patientInfo?.id]); // Depend on patientInfo.id as well

  // Dummy patient info if not provided for testing
  const displayPatientInfo = patientInfo || { patientFullName: "N/A", id: "N/A", patient: {}, testRequests: [{ internalLabService: {} }] };
  const displayVitalInfo = vitalInfo || null;

  console.log(patientInfo);
  console.log(vitalInfo)

  return (
    <Modal open={open} onClose={onClose}>
      <StyledModalBox className="printable-content">

        <LabReportDesigner
          reportData={patientInfo}
          initialTemplateId={currentTemplate}
          initialColor={color}
          template={template}
          review={review}
          compact={compact}
          onSaved={(res) => {
            // handle save callback if needed
          }}
        />

        {/* Header: logo + address (left), report title (center), actions (right) */}
        <Grid container alignItems="center" justifyContent={"space-between"} spacing={2} sx={{ mb: 2 }} className="no-print">

          <Grid item xs={12} sm={4} md={4}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            </Box>
          </Grid>

          {/* Only show action buttons if review is false */}
          {!review && (
            <Grid item xs={12} sm={4} md={4} sx={{ textAlign: { xs: 'left', sm: 'right' } }}>
              <Box sx={{ display: 'flex', justifyContent: { xs: 'flex-start', sm: 'flex-end' }, gap: 1 }}>
                <Tooltip title="Print Report" arrow>
                  <IconButton onClick={handlePrint} color="primary" sx={{ color: '#2e7d32' }}>
                    <MdPrint />
                  </IconButton>
                </Tooltip>

                {/* compact toggle - affects only this modal instance */}
                <Tooltip title={compact ? "Exit compact view" : "Compact view"} arrow>
                  <IconButton
                    onClick={() => setCompact(prev => !prev)}
                    color="primary"
                    sx={{ color: compact ? '#15683a' : '#2e7d32' }}
                  >
                    <MdViewCompact />
                  </IconButton>
                </Tooltip>

                <Tooltip title="Close" arrow>
                  <IconButton onClick={onClose} sx={{ color: '#2e7d32' }}>
                    <MdClose />
                  </IconButton>
                </Tooltip>
              </Box>
            </Grid>
          )}

          {/* Show only close button if review is true */}
          {review && (
            <Grid item xs={12} sm={4} md={4} sx={{ textAlign: { xs: 'left', sm: 'right' } }}>
              <Box sx={{ display: 'flex', justifyContent: { xs: 'flex-start', sm: 'flex-end' }, gap: 1 }}>
                <Tooltip title="Close" arrow>
                  <IconButton onClick={onClose} sx={{ color: '#2e7d32' }}>
                    <MdClose />
                  </IconButton>
                </Tooltip>
              </Box>
            </Grid>
          )}
        </Grid>

        <Grid item mb={4} xs={12} sm={4} md={4} sx={{ textAlign: { xs: 'left', sm: 'center' } }}>
        </Grid>

        {/* Only show send email dialog if review is false */}
        {!review && (
          <SendEmailDialog
            open={sendEmailOpen}
            onClose={() => {
              setSendEmailOpen(false);
              setEmailError('');
              setEmailForm({ name: '', email: '' });
            }}
          >
            <DialogTitle sx={{
              pb: 1,
              color: '#164e2e',
              fontWeight: 700
            }}>
              Send Lab Report
            </DialogTitle>

            <DialogContent>
              <Box sx={{ mt: 2 }}>
                {emailError && (
                  <Typography
                    color="error"
                    variant="body2"
                    sx={{ mb: 2 }}
                  >
                    {emailError}
                  </Typography>
                )}

                <TextField
                  fullWidth
                  label="Recipient Name"
                  variant="outlined"
                  value={emailForm.name}
                  onChange={(e) => setEmailForm(prev => ({
                    ...prev,
                    name: e.target.value
                  }))}
                  sx={{ mb: 2 }}
                />

                <TextField
                  fullWidth
                  label="Email Address"
                  type="email"
                  variant="outlined"
                  value={emailForm.email}
                  onChange={(e) => setEmailForm(prev => ({
                    ...prev,
                    email: e.target.value
                  }))}
                />
              </Box>
            </DialogContent>

            <DialogActions sx={{ px: 3, pb: 3 }}>
              <Button
                onClick={() => {
                  setSendEmailOpen(false);
                  setEmailError('');
                  setEmailForm({ name: '', email: '' });
                }}
                variant="outlined"
                sx={{
                  color: '#2e7d32',
                  borderColor: '#2e7d32',
                  '&:hover': {
                    borderColor: '#1b5e20'
                  }
                }}
              >
                Cancel
              </Button>
              <Button
                onClick={handleSendEmail}
                variant="contained"
                disabled={sending}
                sx={{
                  bgcolor: '#2e7d32',
                  '&:hover': {
                    bgcolor: '#1b5e20'
                  }
                }}
              >
                {sending ? 'Sending...' : 'Send Report'}
              </Button>
            </DialogActions>
          </SendEmailDialog>
        )}

      </StyledModalBox>


    </Modal>
  );
};

export default LabReportTable;

// Add this with your other styled components
const SendEmailDialog = styled(Dialog)(({ theme }) => ({
  '& .MuiDialog-paper': {
    borderRadius: theme.shape.borderRadius * 2,
    padding: theme.spacing(2),
    maxWidth: 500,
    backgroundColor: '#f8fbf9',
  },
  '& .MuiDialogTitle-root': {
    borderBottom: '1px solid #e0e0e0',
  },
  '& .MuiDialogContent-root': {
    padding: theme.spacing(3),
  },
  '& .MuiDialogActions-root': {
    padding: theme.spacing(2, 3),
  },
  '& .MuiTextField-root': {
    '& .MuiOutlinedInput-root': {
      '&:hover fieldset': {
        borderColor: '#2e7d32',
      },
      '&.Mui-focused fieldset': {
        borderColor: '#2e7d32',
      },
    },
    '& .MuiInputLabel-root.Mui-focused': {
      color: '#2e7d32',
    },
  },
}));